import React from 'react';
import { FiBox } from 'react-icons/fi';
import { Canvas } from '@react-three/fiber';
import {
  OrbitControls,
  Box,
  Sphere,
  Cylinder,
  Plane,
  Html,
  Text as DreiText,
} from '@react-three/drei';
import * as THREE from 'three';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// Helper components for cleaner 3D scenes
const SceneWrapper = ({ children, cameraPosition = [6, 6, 6] }) => (
  <div
    style={{
      height: '400px',
      background: 'var(--liwaza-surface-container-lowest)',
      borderRadius: '12px',
      border: '1px solid var(--liwaza-outline-variant)',
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
    }}
  >
    <Canvas camera={{ position: cameraPosition, fov: 50 }}>
      <ambientLight intensity={0.8} />
      <pointLight position={[10, 10, 10]} intensity={1} />
      <pointLight position={[-10, -10, -10]} intensity={0.5} />

      {children}

      <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
    </Canvas>
  </div>
);

const ShapeLabel = ({
  children,
  position,
  color = 'white',
  bgColor = 'rgba(0,0,0,0.6)',
}) => (
  <Html position={position} center>
    <div
      style={{
        background: bgColor,
        color: color,
        padding: '0.3rem 0.7rem',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      }}
    >
      {children}
    </div>
  </Html>
);

// 3D Visual Components
const BasicVolumesVisual = () => (
  <SceneWrapper cameraPosition={[0, 2.5, 12]}>
    <group position={[-4.5, 0.75, 0]}>
      <Box args={[1.5, 1.5, 1.5]}>
        <meshStandardMaterial color="#2563eb" />
      </Box>
      <ShapeLabel position={[0, 1.3, 0]} bgColor="rgba(37, 99, 235, 0.9)">
        Cube (V=s³)
      </ShapeLabel>
    </group>

    <group position={[-1.5, 1, 0]}>
      <Box args={[1, 2, 1.5]}>
        <meshStandardMaterial color="#10b981" />
      </Box>
      <ShapeLabel position={[0, 1.6, 0]} bgColor="rgba(16, 185, 129, 0.9)">
        Prism (V=lwh)
      </ShapeLabel>
    </group>

    <group position={[1.5, 1, 0]}>
      <Sphere args={[1, 32, 32]}>
        <meshStandardMaterial color="#f59e0b" />
      </Sphere>
      <ShapeLabel position={[0, 1.6, 0]} bgColor="rgba(245, 158, 11, 0.9)">
        Sphere (V=(4/3)πr³)
      </ShapeLabel>
    </group>

    <group position={[4.5, 1, 0]}>
      <Cylinder args={[0.8, 0.8, 2, 32]}>
        <meshStandardMaterial color="#ef4444" />
      </Cylinder>
      <ShapeLabel position={[0, 1.6, 0]} bgColor="rgba(239, 68, 68, 0.9)">
        Cylinder (V=πr²h)
      </ShapeLabel>
    </group>

    <Plane args={[15, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const CubeSurfaceAreaVisual = () => (
  <SceneWrapper cameraPosition={[0, 2, 10]}>
    <group position={[-3, 0.5, 0]}>
      <ShapeLabel position={[0, 1.5, 0]}>Solid Cube</ShapeLabel>
      <Box args={[1, 1, 1]}>
        <meshStandardMaterial color="#2563eb" />
      </Box>
      <DreiText
        position={[0, -0.8, 0]}
        fontSize={0.2}
        color="black"
        textAlign="center"
        anchorX="center"
      >
        SA = 6s²
      </DreiText>
    </group>

    <group position={[1.5, 0.5, 0]}>
      <ShapeLabel position={[0, 1.8, 0]}>Unfolded Net</ShapeLabel>
      <Plane args={[1, 1]} position={[0, 0, 0]}>
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      <Plane args={[1, 1]} position={[1, 0, 0]}>
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      <Plane args={[1, 1]} position={[-1, 0, 0]}>
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      <Plane args={[1, 1]} position={[0, 1, 0]}>
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      <Plane args={[1, 1]} position={[0, -1, 0]}>
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      <Plane args={[1, 1]} position={[2, 0, 0]}>
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
    </group>

    <Plane
      args={[15, 8]}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.2, 0]}
    >
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const CylinderSurfaceAreaVisual = () => (
  <SceneWrapper cameraPosition={[0, 2, 10]}>
    <group position={[-3, 1, 0]}>
      <ShapeLabel position={[0, 2.5, 0]}>Solid Cylinder</ShapeLabel>
      <Cylinder args={[0.8, 0.8, 2, 32]}>
        <meshStandardMaterial color="#10b981" />
      </Cylinder>
      <DreiText
        position={[0, -1.3, 0]}
        fontSize={0.2}
        color="black"
        textAlign="center"
        anchorX="center"
        maxWidth={3}
      >
        SA = 2πr² + 2πrh
      </DreiText>
    </group>

    <group position={[1.5, 1, 0]}>
      <ShapeLabel position={[0, 2.5, 0]}>Unfolded Net</ShapeLabel>
      <Cylinder args={[0.8, 0.8, 0.01, 32]} position={[0, 1.5, 0]}>
        <meshStandardMaterial color="#10b981" side={THREE.DoubleSide} />
      </Cylinder>
      <Cylinder args={[0.8, 0.8, 0.01, 32]} position={[0, -1.5, 0]}>
        <meshStandardMaterial color="#10b981" side={THREE.DoubleSide} />
      </Cylinder>
      <Plane args={[2 * Math.PI * 0.8, 2]} position={[0, 0, 0]}>
        <meshStandardMaterial color="#10b981" side={THREE.DoubleSide} />
      </Plane>
    </group>

    <Plane args={[15, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const VolumeComparisonVisual = () => (
  <SceneWrapper cameraPosition={[0, 2.5, 12]}>
    <DreiText
      position={[0, 3.5, 0]}
      fontSize={0.3}
      color="black"
      textAlign="center"
      anchorX="center"
      maxWidth={8}
    >
      Volume Comparison (Fixed Dimension 's')
    </DreiText>

    <group position={[-4, 1, 0]}>
      <Box args={[2, 2, 2]}>
        <meshStandardMaterial color="#2563eb" />
      </Box>
      <ShapeLabel position={[0, 1.5, 0]}>Cube (V = s³)</ShapeLabel>
      <DreiText
        position={[0, -1.3, 0]}
        fontSize={0.25}
        color="black"
        textAlign="center"
        anchorX="center"
      >
        V = 8.00
      </DreiText>
    </group>

    <group position={[0, 1, 0]}>
      <Cylinder args={[1, 1, 2, 32]}>
        <meshStandardMaterial color="#10b981" />
      </Cylinder>
      <ShapeLabel position={[0, 1.5, 0]}>Cylinder (V ≈ 0.785s³)</ShapeLabel>
      <DreiText
        position={[0, -1.3, 0]}
        fontSize={0.25}
        color="black"
        textAlign="center"
        anchorX="center"
      >
        V ≈ 6.28
      </DreiText>
    </group>

    <group position={[4, 1, 0]}>
      <Sphere args={[1, 32, 32]}>
        <meshStandardMaterial color="#f59e0b" />
      </Sphere>
      <ShapeLabel position={[0, 1.5, 0]}>Sphere (V ≈ 0.524s³)</ShapeLabel>
      <DreiText
        position={[0, -1.3, 0]}
        fontSize={0.25}
        color="black"
        textAlign="center"
        anchorX="center"
      >
        V ≈ 4.19
      </DreiText>
    </group>

    <Plane args={[15, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const PracticalApplicationsVisual = () => (
  <SceneWrapper cameraPosition={[0, 2.5, 14]}>
    <group position={[-4, 1, 0]}>
      <ShapeLabel position={[0, 2.5, 0]}>Water Tank</ShapeLabel>
      <Cylinder args={[1, 1, 2, 64]}>
        <meshStandardMaterial color="#3b82f6" />
      </Cylinder>
    </group>

    <group position={[0, 0.75, 0]}>
      <ShapeLabel position={[0, 2, 0]}>Storage Box</ShapeLabel>
      <Box args={[2, 1.5, 2.5]}>
        <meshStandardMaterial color="#10b981" />
      </Box>
    </group>

    <group position={[4, 1, 0]}>
      <ShapeLabel position={[0, 2, 0]}>Sports Ball</ShapeLabel>
      <Sphere args={[1, 32, 32]}>
        <meshStandardMaterial color="#f59e0b" />
      </Sphere>
    </group>

    <Plane args={[15, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const VolumesLesson = () => {
  const sections = [
    {
      id: 'basic-volumes',
      title: 'Basic Volume Formulas',
      content: {
        introduction:
          'Volume measures the amount of space inside a 3D shape. Each shape has a specific formula based on its geometric properties.',
        concepts: [
          {
            title: 'Volume of Prisms',
            explanation:
              'The volume of any prism (including cubes and rectangular prisms) is the area of its base multiplied by its height.',
            visual: <BasicVolumesVisual />,
            example: {
              problem:
                'A rectangular swimming pool is 10m long, 5m wide, and 2m deep. How much water can it hold?',
              solution: [
                'Volume = §\\text{length} \\times \\text{width} \\times \\text{height}§',
                'V = §10m \\times 5m \\times 2m§',
                'V = §100§ m³',
              ],
            },
          },
          {
            title: 'Volume of Cylinders',
            explanation: 'Volume = §\\pi r^2 h§',
            visual: (
              <BasicVolumesVisual>
                <mesh rotation={[Math.PI / 2, 0, 0]}>
                  <cylinderGeometry args={[1, 1, 2.5, 32]} />
                  <meshStandardMaterial color="#f59e0b" wireframe />
                  <ShapeLabel position={[0, -2, 0]}>Cylinder</ShapeLabel>
                </mesh>
              </BasicVolumesVisual>
            ),
            example: {
              problem:
                'A cylindrical can has a radius of 4 cm and a height of 10 cm. Find its volume.',
              solution: [
                'V = §\\pi r^2 h§',
                'V = §\\pi (4^2)(10)§',
                'V = §\\pi (16)(10) = 160\\pi§ cm³',
              ],
            },
          },
          {
            title: 'Volume of Pyramids & Cones',
            explanation:
              'The volume of a pyramid or cone is §1/3§ the volume of a prism or cylinder with the same base and height.',
            visual: (
              <BasicVolumesVisual>
                <mesh rotation={[Math.PI / 2, 0, 0]}>
                  <coneGeometry args={[1.5, 3, 32]} />
                  <meshStandardMaterial color="#ef4444" wireframe />
                  <ShapeLabel position={[0, -2.5, 0]}>Cone</ShapeLabel>
                </mesh>
              </BasicVolumesVisual>
            ),
            example: {
              problem:
                'A cone has a radius of 3 and height of 5. What is its volume?',
              solution: [
                'Volume = §(1/3)\\pi r^2 h§',
                'V = §(1/3)\\pi (3^2)(5)§',
                'V = §(1/3)\\pi (9)(5) = 15\\pi§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'surface-area',
      title: 'Surface Area',
      content: {
        introduction:
          "Surface area measures the total area of all faces of a 3D shape. It's often needed alongside volume in practical problems.",
        concepts: [
          {
            title: 'Unfolding a Cube',
            explanation:
              'Surface area is the sum of the areas of all faces. For a cube, this is the area of its six square faces.',
            visual: <CubeSurfaceAreaVisual />,
            example: {
              problem:
                'A cube has a side length of 5. What is its total surface area?',
              solution: [
                'Area of one face = §5 \\times 5 = 25§',
                'A cube has 6 faces.',
                'Total Surface Area = §6 \\times 25 = 150§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'volume-comparisons',
      title: 'Volume Comparisons',
      content: {
        introduction:
          "For a fixed characteristic dimension 's' (e.g., side of a cube, diameter of a sphere), the volumes of different shapes can be directly compared.",

        concepts: [
          {
            title: 'Doubling Dimensions',
            explanation:
              'If you double the linear dimensions of a solid, its volume increases by a factor of §2^3 = 8§.',
            visual: <VolumeComparisonVisual />,
            example: {
              problem:
                'A box has a volume of 10. If all its sides are doubled, what is the new volume?',
              solution: [
                'The scaling factor is 2.',
                'New Volume = §\\text{Old Volume} \\times (\\text{factor})^3§',
                'New Volume = §10 \\times 2^3 = 10 \\times 8 = 80§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'applications',
      title: 'Real-World Applications',
      content: {
        introduction:
          'Volume calculations are essential in engineering, architecture, cooking, and many other fields.',

        concepts: [
          {
            title: 'Filling a Container',
            explanation:
              'These problems often require calculating the volume of a container and then determining how many smaller items can fit inside or how long it takes to fill.',
            visual: <PracticalApplicationsVisual />,
            example: {
              problem:
                'A rectangular tank (50cm x 20cm x 10cm) is filled with water at a rate of §100 \\frac{cm^3}{second}§. How long to fill it?',
              solution: [
                'Volume of tank = §50 \\times 20 \\times 10 = 10000§ cm³',
                'Rate = §100§ cm³/s',
                'Time = §\\frac{\\text{Total Volume}}{\\text{Rate}} = \\frac{10000}{100} = 100§ seconds',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is the volume of a cube with side length 5?',
      options: ['125', '25', '75', '100', '150'],
      correct: 0,
      explanation: 'Volume of cube = s³ = 5³ = 125 cubic units',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'A cylinder has radius 3 and height 4. What is its volume?',
      options: ['36π', '24π', '12π', '48π', '18π'],
      correct: 0,
      explanation: 'Volume = §\\pi r^2 h = \\pi(3)^2(4) = \\pi(9)(4) = 36\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'If a sphere has radius 3, what is its volume?',
      options: ['36π', '48π', '24π', '12π', '72π'],
      correct: 0,
      explanation:
        'Volume = §\\frac{4}{3}\\pi r^3 = \\frac{4}{3}\\pi(3)^3 = \\frac{4}{3}\\pi(27) = 36\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'A cone has radius 4 and height 9. What is its volume?',
      options: ['48π', '144π', '36π', '72π', '96π'],
      correct: 0,
      explanation:
        'Volume = §\\frac{1}{3}\\pi r^2 h = \\frac{1}{3}\\pi(4)^2(9) = \\frac{1}{3}\\pi(16)(9) = 48\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question: 'The surface area of a sphere with radius 5 is:',
      options: ['100π', '75π', '125π', '150π', '200π'],
      correct: 0,
      explanation:
        'Surface area = §4\\pi r^2 = 4\\pi(5)^2 = 4\\pi(25) = 100\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A rectangular box has dimensions 2, 3, and 4. What is its volume?',
      options: ['24', '12', '9', '20', '18'],
      correct: 0,
      explanation:
        'Volume = §l \\times w \\times h = 2 \\times 3 \\times 4 = 24§',
    },
    {
      type: 'timed',
      timeLimit: 135,
      question: 'A cube has a volume of 27. What is its surface area?',
      options: ['54', '36', '81', '27', '64'],
      correct: 0,
      explanation:
        'Volume = §s^3 = 27§, so side §s=3§. Surface area = §6s^2 = 6 \\times 3^2 = 54§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A cylinder has a volume of §45\\pi§ and a radius of 3. What is its height?',
      options: ['5', '3', '15', '10', '9'],
      correct: 0,
      explanation:
        'V = §\\pi r^2 h§. §45\\pi = \\pi (3^2) h§. §45\\pi = 9\\pi h§. So §h=5§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'If the side of a cube is doubled, its volume increases by what factor?',
      options: ['8', '4', '2', '16', '6'],
      correct: 0,
      explanation:
        'Let side be s. §V_{\\text{old}} = s^3§. New side = 2s. §V_{\\text{new}} = (2s)^3 = 8s^3§. Factor is 8.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'A cone has a volume of §12\\pi§ and its height is 4. What is the radius of the base?',
      options: ['3', '4', '5', '6', '9'],
      correct: 0,
      explanation:
        '§V = (1/3)\\pi r^2 h§. §12\\pi = (1/3)\\pi r^2 (4)§. §36\\pi = 4\\pi r^2§. §r^2=9§, so §r=3§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'What is the surface area of a rectangular prism with dimensions 2, 3, 4?',
      options: ['52', '48', '24', '60', '40'],
      correct: 0,
      explanation:
        'SA = §2(lw + lh + wh) = 2(2\\times3 + 2\\times4 + 3\\times4) = 2(6+8+12) = 2(26) = 52§.',
    },
    {
      type: 'timed',
      timeLimit: 125,
      question:
        'A cylindrical tank with radius 1m and height 5m is filled with water. What is the volume in m³?',
      options: ['§5\\pi§', '§10\\pi§', '§25\\pi§', '§15\\pi§', '§50\\pi§'],
      correct: 0,
      explanation: 'Volume = §\\pi r^2 h = \\pi(1^2)(5) = 5\\pi§ m³.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'A cube has a surface area of 150. What is its volume?',
      options: ['125', '216', '64', '100', '250'],
      correct: 0,
      explanation:
        'SA = §6s^2 = 150§. §s^2=25§, so §s=5§. Volume = §s^3 = 5^3 = 125§.',
    },
    {
      type: 'untimed',
      question:
        'What is the volume of a rectangular prism with sides 5, 6, and 7?',
      options: ['210', '180', '240', '150', '200'],
      correct: 0,
      explanation: 'Volume = §5 \\times 6 \\times 7 = 210§.',
    },
    {
      type: 'untimed',
      question: 'What is the surface area of a cube with side length 4?',
      options: ['96', '64', '48', '24', '16'],
      correct: 0,
      explanation: 'Surface area = 6s² = 6(4)² = 6(16) = 96 square units',
    },
    {
      type: 'untimed',
      question:
        'A cylinder has radius 2 and height 5. What is its surface area?',
      options: ['28π', '20π', '24π', '16π', '32π'],
      correct: 0,
      explanation:
        'Surface area = 2πr² + 2πrh = 2π(2)² + 2π(2)(5) = 8π + 20π = 28π',
    },
    {
      type: 'untimed',
      question:
        'Two cubes have edge lengths in ratio 2:3. Their volume ratio is:',
      options: ['8:27', '2:3', '4:9', '1:1', '6:9'],
      correct: 0,
      explanation:
        'Volume ratio = (2)³:(3)³ = 8:27. Volume scales as the cube of the linear dimension.',
    },
    {
      type: 'untimed',
      question: 'Which shape has the smallest surface area for a given volume?',
      options: ['Sphere', 'Cube', 'Cylinder', 'Cone', 'Rectangular prism'],
      correct: 0,
      explanation:
        'Among all shapes with the same volume, a sphere has the minimum surface area (isoperimetric inequality).',
    },
    {
      type: 'untimed',
      question:
        'A cylinder has a radius of 3 cm and height of 7 cm. What is its volume? (Use π ≈ 3.14)',
      options: ['≈197.8 cm³', '≈63 cm³', '≈94.2 cm³', '≈113 cm³', '≈141 cm³'],
      correct: 0,
      explanation: 'Volume = πr²h = 3.14 × 3² × 7 = 3.14 × 9 × 7 ≈ 197.82 cm³.',
    },
    {
      type: 'untimed',
      question:
        'A cone has a base radius of 4 cm and height of 9 cm. What is its volume? (Use π ≈ 3.14)',
      options: ['≈150.7 cm³', '≈452 cm³', '≈113 cm³', '≈75.4 cm³', '≈226 cm³'],
      correct: 0,
      explanation:
        'Volume = (1/3)πr²h = (1/3) × 3.14 × 4² × 9 = (1/3) × 3.14 × 16 × 9 ≈ 150.72 cm³.',
    },
    {
      type: 'untimed',
      question:
        'A sphere has a radius of 3 cm. What is its volume? (Use π ≈ 3.14)',
      options: ['≈113 cm³', '≈36 cm³', '≈84.8 cm³', '≈28.3 cm³', '≈56.5 cm³'],
      correct: 0,
      explanation:
        'Volume = (4/3)πr³ = (4/3) × 3.14 × 3³ = (4/3) × 3.14 × 27 ≈ 113.04 cm³.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'A cube has edge length 3. Compare the two quantities.',
      quantityA: 'Volume',
      quantityB: 'Surface area',
      correct: 1,
      explanation:
        'Compare the two quantities directly. Quantity B is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question: 'Which formulas give volume? Select all that apply.',
      options: [
        'Rectangular prism: §lwh§',
        'Cylinder: §pi*r^2*h§',
        'Circle: §pi*r^2§',
        'Cube: §s^3§',
        'Sphere surface area: §4*pi*r^2§',
      ],
      correct: [0, 1, 3],
      explanation: 'Volume formulas include §lwh§, §pi*r^2*h§, and §s^3§.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question:
        'A cylinder has radius 2 and height 5. Enter its volume divided by §pi§.',
      correctAnswer: '20',
      acceptedAnswers: ['20'],
      explanation:
        'Volume is §pi*r^2*h = pi*4*5 = 20pi§, so divided by §pi§ gives 20.',
    },
  ];

  const quickReference = [
    'Cube Volume: §s^3§, SA: §6s^2§',
    'Rect. Prism Volume: §lwh§, SA: §2(lw+lh+wh)§',
    'Cylinder Volume: §\\pi r^2 h§, SA: §2\\pi rh + 2\\pi r^2§',
    'Cone Volume: §(1/3)\\pi r^2 h§',
    'Pyramid Volume: §(1/3) \\times \\text{base area} \\times h§',
    'Sphere Volume: §(4/3)\\pi r^3§, SA: §4\\pi r^2§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Volumes & Surface Area?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding 3D Figures concepts for the GRE Quantitative section',
    aicontext:
      'Master cubes, cylinders, spheres, cones, and their volume and surface area formulas',
  };

  return (
    <LessonTemplate
      lessonId="volumes"
      lessonTitle="Volumes & Surface Area"
      lessonIcon={<FiBox />}
      lessonDescription="Master cubes, prisms, cylinders, spheres, and volume calculations"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="volumes" />}
    />
  );
};

export default VolumesLesson;
